import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LoadingIcon, TopDropdown } from "../../.././../components";
import { getGreenhouses } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";
import { DatePicker } from "antd";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage("dropdowns", null);
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");
  const [plotLoading, setPlotLoading] = useState(false);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const getPlotDates = (plot) =>
    (plot?.harvests || []).map((h) => h.date).sort((a, b) => dayjs(b).diff(a));

  // Helper to locate the greenhouse for a given plot ID.
  // Plot IDs are reused across greenhouses, so prefer the greenhouse hint.
  const findGreenhouseForPlot = (plotId, greenhouses, greenhouseHint = null) => {
    if (greenhouseHint) {
      const direct = (greenhouses || []).find((gh) => gh.id === greenhouseHint);
      if (direct && direct.plots.some((p) => p.id === plotId)) return direct;
    }
    for (const gh of greenhouses || []) {
      if (gh.plots.some((p) => p.id === plotId)) {
        return gh;
      }
    }
    return null;
  };

  const findPlot = (plotId, greenhouseHint = null) =>
    (greenhouseHint && plotData?.find((p) => p.id === plotId && p.greenhouse_id === greenhouseHint)) ||
    plotData?.find((p) => p.id === plotId);

  const syncSelection = (selectedPlot, greenhouseHint = null, preferredDate = null) => {
    if (!selectedPlot) return;

    const selectedGreenhouse =
      (greenhouseHint && greenhouseData.find((g) => g.id === greenhouseHint)) ||
      (selectedPlot.greenhouse_id && greenhouseData.find((g) => g.id === selectedPlot.greenhouse_id)) ||
      findGreenhouseForPlot(selectedPlot.id, greenhouseData, greenhouseHint);

    const nextDates = getPlotDates(selectedPlot);
    const nextDate =
      preferredDate && nextDates.includes(preferredDate)
        ? preferredDate
        : nextDates[0] || null;

    setCurrentPlot(selectedPlot);
    if (selectedGreenhouse) setCurrentGreenhouse(selectedGreenhouse);
    setDateData(nextDates);
    setCurrentDate(nextDate);

    const params = new URLSearchParams(searchParams);
    params.set("plot", selectedPlot.id);
    if (selectedGreenhouse) params.set("greenhouse", selectedGreenhouse.id);
    if (nextDate) params.set("date", nextDate);
    else params.delete("date");
    setSearchParams(params);

    const orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        greenhouse: selectedGreenhouse?.id || orgObj.greenhouse,
        plot: selectedPlot.id,
        date: nextDate || orgObj.date,
      },
    });
  };

  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const items = Array.isArray(res.data?.data) ? res.data?.data : [];
    const data = items?.map((greenhouse) => {
      return {
        name: greenhouse.name,
        id: greenhouse.id,
        plots: greenhouse.plots.map((plot) => {
          return {
            ...plot,
            greenhouse_name: greenhouse.name,
            greenhouse_id: greenhouse.id,
          };
        }),
      };
    });
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  };

  const disabledDate = (current) => {
    const dayjsDates = dateData.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((isEnabled, date) => {
        if (isEnabled) {
          return true;
        } else {
          return date.isSame(current, "day");
        }
      }, false)
    );
  };

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      const backendDateString = dayjs(dateString, UI_DATE_FORMAT).format(BACKEND_DATE_FORMAT);
      setCurrentDate(backendDateString);
      const params = new URLSearchParams(searchParams);
      params.set("date", backendDateString);
      setSearchParams(params);
    }
  };

  // Fetch greenhouses on site/crop change
  useEffect(() => {
    if (currentSite && currentOrg) {
      const siteMetaData = currentSite.metadata[currentCrop?.name] || {};
      setSiteMetadata(siteMetaData);

      if (siteMetaData?.greenhouse_name) {
        setGreenHouseLabel(siteMetaData.greenhouse_name);
      }
      if (siteMetaData?.block_name) {
        setBlockLabel(siteMetaData.block_name);
      }
      fetchGreenhouses(currentOrg, currentSite.id, currentCrop?.id);
    }
  }, [currentSite, currentCrop, currentOrg]);

  /**
   * Flatten ALL plots from all greenhouses into a single array,
   * so the Block dropdown displays everything.
   */
  useEffect(() => {
    if (greenhouseData && greenhouseData.length) {
      // Flatten all plots from all greenhouses
      const allPlots = greenhouseData.flatMap((gh) => gh.plots);

      // Sort them (optional). Check if they are numeric or not:
      const allNamesAreNumeric = allPlots.every((p) => /^\d+$/.test(p.name));
      if (allNamesAreNumeric) {
        allPlots.sort((a, b) => parseInt(a.name) - parseInt(b.name));
      } else {
        allPlots.sort((a, b) => a.name.localeCompare(b.name));
      }

      setPlotData(allPlots);
    } else {
      setPlotData(null);
    }
  }, [greenhouseData, setPlotData]);

  // Whenever plot changes, set the date data
  useEffect(() => {
    if (currentPlot) {
      setDateData(getPlotDates(currentPlot));
    }
  }, [currentPlot, setDateData]);

  /**
   * Whenever we first populate plotData (all possible plots),
   * pick the correct plot from URL or local defaults.
   * Also set the greenhouse that corresponds to that plot.
   */
  useEffect(() => {
    if (plotData && plotData.length) {
      let params = new URLSearchParams(searchParams);
      let plotIdFromUrlParams = params.get("plot");
      let greenhouseIdFromUrlParams = params.get("greenhouse");
      let dateFromUrlParams = params.get("date");

      if (plotIdFromUrlParams) {
        console.log("SETTING PLOT FROM URL PARAMS");
        const plotFromUrl = findPlot(plotIdFromUrlParams, greenhouseIdFromUrlParams);
        if (plotFromUrl) {
          syncSelection(plotFromUrl, greenhouseIdFromUrlParams, dateFromUrlParams);
        } else {
          console.log("SETTING FIRST PLOT OF DATA");
          syncSelection(plotData[0], plotData[0]?.greenhouse_id, dateFromUrlParams);
        }
      } else if (!settingDefaults.plot) {
        console.log("SETTING DEFAULT PLOT FROM LOCALSTORAGE");
        let defaultPlotId = defaultDropdown && defaultDropdown[currentOrg]?.plot;
        let defaultGreenhouseId = defaultDropdown && defaultDropdown[currentOrg]?.greenhouse;
        let defaultDate = defaultDropdown && defaultDropdown[currentOrg]?.date;

        if (defaultPlotId) {
          let defaultPlotValue = findPlot(defaultPlotId, defaultGreenhouseId);
          if (defaultPlotValue) {
            syncSelection(defaultPlotValue, defaultGreenhouseId, defaultDate);
          } else {
            // fallback
            syncSelection(plotData[0], plotData[0]?.greenhouse_id, defaultDate);
          }
        } else {
          syncSelection(plotData[0], plotData[0]?.greenhouse_id, defaultDate);
        }
        setSettingDefaults({ ...settingDefaults, plot: true });
      } else {
        // fallback if no param or local default
        console.log("SETTING FIRST PLOT OF DATA");
        syncSelection(plotData[0], plotData[0]?.greenhouse_id);
      }
    } else {
      setCurrentPlot(null);
      setDateData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotData]);

  // If the user changes the plot by interacting with the dropdown,
  // update greenhouse, date, local storage, and URL together.
  const handlePlotChange = (rawValue) => {
    const [greenhouseId, plotId] = rawValue.split("---");
    const selectedPlot = findPlot(plotId, greenhouseId);
    syncSelection(selectedPlot, greenhouseId);
  };

  // Select the date from URL (if available), or fall back to the first date
  useEffect(() => {
    let params = new URLSearchParams(searchParams);
    let dateFromUrl = params.get("date");
    if (dateFromUrl) {
      if (dateData && dateData.includes(dateFromUrl)) {
        setCurrentDate(dateFromUrl);
      } else {
        const dateString = dateData ? dateData[0] : null;
        setCurrentDate(dateString);
      }
    } else {
      const dateString = dateData ? dateData[0] : null;
      setCurrentDate(dateString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateData]);

  /**
   * (Optional) If you still want to store currentGreenhouse in the URL params
   * and in local storage, you can keep this effect:
   */
  useEffect(() => {
    if (currentGreenhouse) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGreenhouse]);

  // Whenever currentPlot changes, update the URL param + local storage
  useEffect(() => {
    if (currentPlot) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // Set loading state for dropdowns
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || plotLoading || greenhouseLoading || !currentDate,
    );
  }, [
    orgLoading,
    cropLoading,
    siteLoading,
    plotLoading,
    greenhouseLoading,
    currentDate,
    setDropdownLoading,
  ]);

  return (
    <>
      {orgLoading || siteLoading || plotLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "").toLowerCase().localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          {/* Site dropdown => ID-based */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "").toLowerCase().localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop dropdown => ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "").toLowerCase().localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />
          {/*<TopDropdown*/}
          {/*  label={`Select ${greenHouseLabel}:`}*/}
          {/*  val={currentGreenhouse?.id ?? undefined}*/}
          {/*  placeholder={`Select ${greenHouseLabel}`}*/}
          {/*  classString="flex-1 min-w-[150px] sm:mb-2"*/}
          {/*  changeCallback={(id) => {*/}
          {/*    const greenhouseObj = greenhouseData.find((gh) => gh.id === id);*/}
          {/*    setCurrentGreenhouse(greenhouseObj);*/}
          {/*  }}*/}
          {/*  showSearch*/}
          {/*  optionFilterProp="children"*/}
          {/*  filterOption={(input, option) =>*/}
          {/*    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())*/}
          {/*  }*/}
          {/*  data={(() => {*/}
          {/*    if (!greenhouseData) return [];*/}
          {/*    const allNamesAreNumeric = greenhouseData.every((gh) =>*/}
          {/*      /^\d+$/.test(gh.name),*/}
          {/*    );*/}

          {/*    const sortedGreenhouseData = [...greenhouseData];*/}
          {/*    if (allNamesAreNumeric) {*/}
          {/*      sortedGreenhouseData.sort(*/}
          {/*        (a, b) => parseInt(a.name) - parseInt(b.name),*/}
          {/*      );*/}
          {/*    } else {*/}
          {/*      sortedGreenhouseData.sort((a, b) =>*/}
          {/*        a.name.localeCompare(b.name),*/}
          {/*      );*/}
          {/*    }*/}

          {/*    return sortedGreenhouseData.map((greenhouse) => ({*/}
          {/*      label: greenhouse.name,*/}
          {/*      value: greenhouse.id,*/}
          {/*    }));*/}
          {/*  })()}*/}
          {/*/>*/}

          <TopDropdown
            label={`Select ${blockLabel}:`}
            val={
              currentGreenhouse && currentPlot
                ? `${currentGreenhouse.id}---${currentPlot.id}`
                : undefined
            }
            placeholder={`Select ${blockLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={handlePlotChange}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: 300 }}
            data={(() => {
              if (!plotData) return [];
              const allNamesAreNumeric = plotData.every((plot) => /^\d+$/.test(plot.name));

              const sortedPlotData = [...plotData];
              if (allNamesAreNumeric) {
                sortedPlotData.sort((a, b) => parseInt(a.name) - parseInt(b.name));
              } else {
                sortedPlotData.sort((a, b) => a.name.localeCompare(b.name));
              }

              return sortedPlotData.map((plot) => ({
                label: `${plot.greenhouse_name} : ${plot.name}`,
                value: plot.greenhouse_id + "---" + plot.id,
              }));
            })()}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Date of Data Collection:
            </div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[150px] sm:mb-2"
              disabledDate={disabledDate}
              format={UI_DATE_FORMAT}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
